import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const kebab = (k) => k.replace(/_/g, "-");
const DOT = { done: "bg-foreground/35", active: "bg-primary", blocked: "bg-rose-500", upcoming: "bg-border", na: "bg-transparent ring-1 ring-border" };
const STATE_TEXT = { done: "fullført", active: "pågår", blocked: "blokkert", upcoming: "ikke startet", na: "ikke aktuelt" };

/**
 * Livslinjen – ten parallel tracks (prosjektering … demontering), each with its own state, short status, date and
 * blockers. Not a wizard: several tracks are active at once; the main phase (`mainTrack`) is emphasised.
 */
export const PhaseLine = ({ tracks, mainTrack, onSelect, className }) => (
  <div className={cn("-mx-1 overflow-x-auto px-1 pb-1 scrollbar-thin", className)} data-testid="project-phase-line">
    <ol className="grid min-w-[960px] grid-cols-10 gap-1.5" aria-label="Prosjektets livslinje">
      {tracks.map((t) => {
        const main = t.key === mainTrack;
        return (
          <li key={t.key} className="min-w-0">
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <button type="button" onClick={() => onSelect?.(t)} data-testid={`project-phase-${kebab(t.key)}`} data-track-state={t.state} data-track-main={main || undefined}
                  className={cn(
                    "relative flex h-full w-full flex-col gap-0.5 rounded-lg border px-2.5 py-2 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
                    main ? "border-primary/35 bg-primary/[0.06] ring-1 ring-primary/25" : "border-border/60 bg-card hover:bg-muted/50",
                    t.state === "blocked" && !main && "border-rose-300/60 dark:border-rose-800/60",
                    (t.state === "upcoming" || t.state === "na") && "text-muted-foreground"
                  )}>
                  <div className="flex items-center justify-between gap-1">
                    <span className={cn("truncate text-[12px] font-medium leading-4", t.state === "done" && "text-muted-foreground", t.state === "na" && "text-muted-foreground/70")}>{t.label}</span>
                    <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", DOT[t.state])} aria-label={STATE_TEXT[t.state]} />
                  </div>
                  <div className={cn("truncate text-[11px] leading-4", t.state === "blocked" ? "font-medium text-rose-700 dark:text-rose-300" : t.state === "active" || main ? "text-foreground/80" : "text-muted-foreground")}>{t.status || "—"}</div>
                  <div className={cn("font-mono text-[10.5px] tabular leading-4", t.late ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground")}>
                    {t.date ? `${t.estimated ? "~" : ""}${fmtDate(t.date, "d. MMM yy")}` : "\u00a0"}
                  </div>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[280px] border bg-popover px-3 py-2 text-popover-foreground shadow-pop">
                <div className="text-[12px] font-medium">{t.label} <span className="font-normal text-muted-foreground">· {STATE_TEXT[t.state]}{main ? " · hovedfase" : ""}</span></div>
                {t.status && <div className="text-[11.5px]">{t.status}</div>}
                {t.date && <div className={cn("font-mono text-[11px] tabular", t.late ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground")}>{t.estimated ? "Estimert " : ""}{fmtDate(t.date, "d. MMM yyyy")}{t.late ? " · planlagt dato passert" : ""}</div>}
                {t.owner && <div className="text-[11px] text-muted-foreground">{t.owner}</div>}
                {t.source && <div className="text-[11px] text-muted-foreground">{t.source}</div>}
                {t.blockers.length > 0 && <ul className="mt-1 space-y-0.5 text-[11.5px] text-rose-600 dark:text-rose-300">{t.blockers.map((b) => <li key={b}>• {b}</li>)}</ul>}
              </TooltipContent>
            </Tooltip>
          </li>
        );
      })}
    </ol>
  </div>
);
